import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  People as PeopleFilledIcon,
} from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  AccordionSummary,
  Box,
  ButtonGroup,
  IconButton,
  MenuItem,
  Select,
  styled,
  Tab as MuiTab,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs as MuiTabs,
  Theme,
  Typography,
} from '@mui/material';
import { DynamicTooltipChip } from 'src/components/dynamicTooltipChip/DynamicTooltipChip';
import { LOCATION_HEADER_HEIGHT } from 'src/constants';
import { theme } from 'src/utils';

export const Container = styled(TableContainer)`
  display: flex; 
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

export const HeaderContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 0;
`;

export const CustomTableContainer = styled(TableContainer)`
  width: 100%;
  height: calc(100% - ${LOCATION_HEADER_HEIGHT}px);
  overflow-y: auto;
`;

export const CustomTableHead = styled(TableHead)(
  ({ theme }) => `
    th {
      border-bottom: 2px solid ${theme.palette.brand.black};
      font-weight : 700;
      font-size: 1rem;
    }
  `,
);

export const ButtonGroupWrapper = styled(ButtonGroup)(
  ({ theme }) =>
    `
    border: 1px solid ${theme.palette.primary.main};
    width: 136px;
    height: 36px;
    display: flex;
    overflow: hidden;
  `,
);

export const StyledIconButton = styled(IconButton)<{ $active?: boolean }>(
  ({ $active, theme }) => ({
    backgroundColor: $active
      ? theme.palette.primary.main
      : theme.palette.primary.lightest,
    color: $active
      ? theme.palette.primary.lightest
      : theme.palette.primary.main,
    borderRadius: 0,
    flex: 1,
    '&:hover': {
      backgroundColor: $active
        ? theme.palette.primary.main
        : theme.palette.primary.lightest,
    },
  }),
);

export const CustomTableRow = styled(TableRow)(
  ({ theme }) => `
 border-bottom: 1px solid ${theme.palette.brand.disable};
`,
);

export const KeyboardArrowUpIconStyled = styled(KeyboardArrowUpIcon)<{
  columnkey: string;
  sortdirectiondirection: string;
  sortdirectionkey: string;
}>`
  color: ${(props: {
    columnkey: string;
    sortdirectionkey: string;
    sortdirectiondirection: string;
    theme: { palette: { primary: { main: string } } };
  }) =>
    props.columnkey === props.sortdirectionkey &&
    props.sortdirectiondirection === 'ascending'
      ? props.theme.palette.primary.main
      : 'inherit'};
`;

export const KeyboardArrowDownIconStyled = styled(KeyboardArrowDownIcon)<{
  columnkey: string;
  sortdirectiondirection: string;
  sortdirectionkey: string;
}>`
  color: ${(props: {
    columnkey: string;
    sortdirectionkey: string;
    sortdirectiondirection: string;
    theme: { palette: { primary: { main: string } } };
  }) =>
    props.columnkey === props.sortdirectionkey &&
    props.sortdirectiondirection === 'descending'
      ? props.theme.palette.primary.main
      : 'inherit'};
`;

export const SortArrowUpIconStyled = styled(KeyboardArrowUpIcon)<{
  active?: boolean;
}>`
  color: ${({ active, theme }: { active?: boolean; theme: Theme }) =>
    active ? theme.palette.primary.main : 'inherit'};
`;

export const SortArrowDownIconStyled = styled(KeyboardArrowDownIcon)<{
  active?: boolean;
}>`
  color: ${({ active, theme }: { active?: boolean; theme: Theme }) =>
    active ? theme.palette.primary.main : 'inherit'};
`;

export const CustomTableCell = styled(TableCell)<{
  $isdoublewidth: string;
  $isAssemblyName?: boolean;
  $isHeader?: boolean;
}>((props) => {
  const { $isdoublewidth, $isAssemblyName, $isHeader } = props;

  return `
    max-width: 35vw;
    overflow: hidden;
    text-overflow: ellipsis;

    width: ${
      $isdoublewidth === 'assemblyName' || $isdoublewidth === 'component_name'
        ? '230px'
        : '10px'
    };

    font-size: 14px;
    font-weight: ${$isHeader ? '600' : 'none'};

    border: ${$isdoublewidth === 'component_name' ? 'none' : ''};
    padding: ${$isdoublewidth === 'component_name' ? '6px 0px' : ''};

    background-color: ${
      $isAssemblyName ? 'transparent' : theme.palette.grey[100]
    };

    & a,
    & p {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-word;
      color: ${$isAssemblyName ? theme.palette.primary.main : 'inherit'};
    }

    svg {
      font-size: 14px;
    }
  `;
});

export const CustomTableCellSchedule = styled(TableCell)<{
  $isdoublewidth?: boolean | string;
  $isHeader?: boolean;
}>(({ theme, $isdoublewidth, $isHeader }) => ({
  fontSize: '14px',
  fontWeight: $isHeader ? 600 : 400,
  width: $isdoublewidth ? '230px' : 'auto',
  padding: $isHeader ? '16px' : '12px 16px',
  backgroundColor: $isHeader ? theme.palette.grey[100] : 'transparent',
  '& svg': {
    fontSize: '14px',
  },
}));

export const SortIcons = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin-left: 4px;
`;

export const TableHeadCell = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;
export const ArrowIcon = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  cursor: pointer;
  line-height: 1;
`;

export const Tabs = styled(MuiTabs)(
  ({ theme }) => `
  border-bottom: 1px solid ${theme.palette.brand.charcoal};
`,
);

export const Tab = styled(MuiTab)(
  ({ theme }) => `
  &.Mui-selected {
    color: ${theme.palette.secondary.dark};
  }
`,
);

export const StyledMenuItem = styled(MenuItem)`
  height: 30px;
  gap: 7px;
  svg {
    color: rgba(24, 28, 32, 0.6);
  }
`;

export const CustomTableCellAlign = styled(CustomTableCell)`
  margin: 0;
  padding: 0;
  text-align: right;
`;
export const RoundedDropdown = styled(Select)(
  ({ theme }) => `
    color:${theme.palette.brand.muted};
    border-radius: 56px;
    width: 220px;
    height: 40px;
    @media (max-width: 1300px) {
      width: auto;
    }
  `,
);
export const DropdownContainer = styled(Box)`
  display: flex;
  justify-content: flex-end;
  padding: 16px;
  gap: 16px;
`;

export const StyledExpandMoreIcon = styled(ExpandMoreIcon)<{
  isexpanded: boolean;
}>(({ theme, isexpanded }) => ({
  transform: isexpanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.2s ease-in-out',
  color: theme.palette.brand.transparentBlack,
}));

export const StyledTableCell = styled(TableCell)`
  width: 48px;
  padding: 0px;
`;
export const StyledTable = styled(Table)`
  width: 100%;
  table-layout: fixed;
`;
export const TableCellContainer = styled(TableCell)`
  padding: 0;
  border: none;
`;
export const LocationOverrideChip = styled(DynamicTooltipChip)(
  ({ theme }) => `
  background-color: ${theme.status.success.light};
  color: ${theme.palette.brand.parsley};
  margin-right: 10px;
  margin-bottom: 4px;
`,
);

export const StyledAccordionSummary = styled(AccordionSummary)({
  padding: 0,
  margin: 0,
  minHeight: 'unset',

  '&.Mui-expanded': {
    minHeight: 'unset',
  },

  '& .MuiAccordionSummary-content': {
    padding: 0,
    margin: 0,

    '&.Mui-expanded': {
      margin: 0,
      padding: 0,
    },
  },

  '&.MuiAccordionSummary-root': {
    padding: 0,
  },
});
export const FlexRowCenter = styled(Box)`
  display: flex;
  align-items: center;
`;
export const NoWrapTypography = styled(Typography)`
  margin-right: 8px;
  white-space: nowrap;
`;

export const TopResultContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  gap: 16px;
`;
export const StyledPageMenuItem = styled(MenuItem)`
  width: 100px;
`;
export const BlankStateImg = styled('img')`
  width: 200px;
  height: 161px;
`;

export const BlankStateWrapper = styled(Box)(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 60%;
  margin-top:20px;
  border:1px solid  ${theme.palette.brand.borderGrey};
  border-radius:10px;
  `,
);

export const OverrideEnabledChip = styled(Box)(
  ({ theme }) => `
  display: inline-flex;
  align-items: center;
  gap: 2px;
  width: 150px;
  height: 28px;
  padding: 3px 6px 3px 6px;
  border-radius: 100px;
  background-color: ${theme.palette.secondary.lighter};
  color: ${theme.palette.brand.black};
  opacity: 1;
  font-size: 12px;
  font-weight: 500;
`,
);

export const OverrideEnabledIcon = styled(PeopleFilledIcon)(
  ({ theme }) => `
  font-size: 16px;
  color: ${theme.palette.grey[600]};
  margin-right: 2px;
  font-weight: 500;
`,
);
